/**
 * Revenue Tab Page
 * Overview + Payments, Invoices, Recurring Billing, Generate Invoices, Other Income
 * as horizontal tabs under a single "Revenue" view.
 */

import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LayoutDashboard, CreditCard, FileText, HandCoins, RefreshCw, PlayCircle } from 'lucide-react';
import { RevenueOverview } from './revenue-overview';
import { PaymentsPage } from './payments-page';
import { InvoicesPage } from './invoices-page';
import { RecurringSchedulesPage } from './recurring-schedules-page';
import { GenerateInvoicesPage } from './generate-invoices-page';
import { OtherIncomePage } from './other-income-page';

function getInitialTab(pathname: string) {
  if (pathname.endsWith('/payments')) return 'payments';
  if (pathname.endsWith('/invoices')) return 'invoices';
  if (pathname.endsWith('/recurring')) return 'recurring';
  if (pathname.endsWith('/generate')) return 'generate';
  if (pathname.endsWith('/other-income')) return 'other-income';
  return 'overview';
}

const tabRoutes: Record<string, string> = {
  overview: '/billing/revenue',
  payments: '/billing/payments',
  invoices: '/billing/invoices',
  recurring: '/billing/recurring',
  generate: '/billing/generate',
  'other-income': '/billing/other-income',
};

export function RevenueTabsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => getInitialTab(location.pathname));

  function handleTabChange(value: string) {
    setTab(value);
    navigate(tabRoutes[value] || '/billing/revenue', { replace: true });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Revenue</h1>
        <p className="text-muted-foreground">Track payments received, manage invoices, and record other income</p>
      </div>

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <LayoutDashboard className="h-4 w-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Payments
          </TabsTrigger>
          <TabsTrigger value="invoices" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Invoices
          </TabsTrigger>
          <TabsTrigger value="recurring" className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Recurring Billing
          </TabsTrigger>
          <TabsTrigger value="generate" className="flex items-center gap-2">
            <PlayCircle className="h-4 w-4" />
            Generate Invoices
          </TabsTrigger>
          <TabsTrigger value="other-income" className="flex items-center gap-2">
            <HandCoins className="h-4 w-4" />
            Other Income
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <RevenueOverview />
        </TabsContent>

        <TabsContent value="payments">
          <PaymentsPage />
        </TabsContent>

        <TabsContent value="invoices">
          <InvoicesPage />
        </TabsContent>

        <TabsContent value="recurring">
          <RecurringSchedulesPage />
        </TabsContent>

        <TabsContent value="generate">
          <GenerateInvoicesPage />
        </TabsContent>

        <TabsContent value="other-income">
          <OtherIncomePage />
        </TabsContent>
      </Tabs>
    </div>
  );
}
